import Vue from 'vue'
import { getField, updateField } from 'vuex-map-fields'
import Laravel from '@/services/Laravel'
import { UserModel, MerchantModel, CustomerModel } from '@/models/UserHierarchy'
import router from '@/router/'
import {
  MerchantRoutes,
  CustomerRoutes,
  ADMIN_URI_BASE,
} from '@/constants/RouteNames'
import AllModels from '@/models/index'
import ShopModel from '@/models/ShopModel'
import Mtxs from '@/constants/Mutations'
import {
  parseISO,
  subMinutes,
  differenceInSeconds,
  formatDistanceToNowStrict,
  toDate,
  isPast,
} from 'date-fns'
import _get from 'lodash/get'
import _includes from 'lodash/includes'

export const state = {
  user: {},
  token: null,
  sessionExpiry: null,
} // state

export const mutations = {
  [Mtxs.SET_AUTH_USER](state, user) {
    state.user = user
  },
  [Mtxs.SET_TOKEN](state, token) {
    state.token = token
  },

  [Mtxs.SET_AUTH_EXPIRY](state, expiry) {
    state.sessionExpiry = parseISO(expiry)
  }, // SET_AUTH_EXPIRY

  [Mtxs.SET_AUTH_LOGGED_OUT](state) {
    state.token = null
    state.user = {}
    state.sessionExpiry = null
  }, // SET_AUTH_LOGGED_OUT

  updateField,
}

export const getters = {
  isAuthenticated: (state) => Object.keys(state.user).length,
  getSessionExpiry: (state) =>
    state.sessionExpiry
      ? new Date(state.sessionExpiry)
      : toDate(subMinutes(new Date(), 60)),
  getSessionExpiryInSeconds: (state, getters) =>
    differenceInSeconds(getters.getSessionExpiry, new Date()),
  getSessionExpiryVerbal: (_, getters) =>
    formatDistanceToNowStrict(getters.getSessionExpiry),
  isExpired: (_, getters) => isPast(getters.getSessionExpiry),
  showSessionCountdown: (_, getters) =>
    !getters.isExpired && getters.getSessionExpiryInSeconds < 5 * 60,
  getAuthUser: (state) => state.user,
  getAuthUserModel: () =>
    UserModel.query()
      .where('token', (token) => !!token)
      .first(),
  getAuthUserId: (_, getters) => _get(getters.getAuthUser, 'id'),
  getAuthUserName: (_, getters) => _get(getters.getAuthUser, 'first_name'),
  getAuthUserFullName: (_, getters) => {
    let parts = [
      _get(getters.getAuthUser, 'first_name'),
      _get(getters.getAuthUser, 'last_name'),
    ]
    return parts.filter((p) => !!p).join(' ')
  },
  canSuperAdmin: (state) => _get(state.user, 'is_super_admin'),
  canMerchant: (state, getters) =>
    _get(state.user, 'is_merchant') || getters.canSuperAdmin,
  canCustomer: (state, getters) =>
    getters.isAuthenticated || getters.canMerchant,
  isCustomer: (state, getters) => _get(getters.userRoles, 'is_customer', false),
  userRoles: (state, getters) => {
    return {
      is_super_admin: getters.canSuperAdmin,
      is_merchant: getters.canMerchant,
      is_customer: getters.canCustomer,
    }
  },
  getAuthToken: (state) => _get(state, 'token', ''),

  // Contact / Address Stuff

  getAuthUserStreetAddress: (state, getters) =>
    (_get(getters.getAuthUser, 'street', '') ?? '').trim(),
  getAuthUserZip: (state, getters) =>
    (_get(getters.getAuthUser, 'zip', '') ?? '').trim(),
  getAuthUserAddress: (state, getters) =>
    [getters.getAuthUserStreetAddress, getters.getAuthUserZip]
      .filter((i) => i.trim().length)
      .join(', '),
  getAuthUserHasAddress: (state, getters) =>
    !!getters.getAuthUserAddress.length,
  getAuthUserEmail: (state, getters) =>
    _get(getters.getAuthUser, 'email', '').trim(),

  isAuthUserEmailVerified: (_, getters) =>
    !!_get(getters.getAuthUser, 'email_verified_at', false),

  getAuthUserFamilyDiscount: (state, getters) =>
    Number(_get(getters.getAuthUser, 'family_discount', 0)),

  getIsAdminDrawerMini: (state, getters) =>
    !!Number(_get(getters.getAuthUser, 'is_admin_drawer_mini', false)),

  getField,
}

export const actions = {
  async signup({ commit }, adminData) {
    try {
      let { data: user } = await MerchantModel.signup(adminData)
      // @todo: dispatch setExpiry when this route is wired up
      // ** it's possible it may be implemented via getminimart.com/signup
      // and therefore wouldn't use this app.
      // dispatch('setExpiry', user.expires_at)
      return user
    } catch (error) {
      commit(Mtxs.SET_AUTH_LOGGED_OUT)
      return Promise.reject(error)
    }
  },

  async merchantLogin({ commit, dispatch, rootGetters }, credentials) {
    try {
      let shopId = rootGetters['shop/getShopId']
      let { response: loginResponse } = await MerchantModel.merchantLogin(
        shopId,
        credentials
      )
      // @todo: figure out why this is data.data instead of just plain ol data
      let user = _get(loginResponse, 'data.data')
      dispatch('setUser', user)
      dispatch('setToken', user.token)
      dispatch('setExpiry', user.expires_at)
      dispatch('setupFullStory')
      return user
    } catch (error) {
      commit(Mtxs.SET_AUTH_LOGGED_OUT)
      return Promise.reject(error)
    }
  },

  async customerSignup({ commit, dispatch }, signupData) {
    try {
      let { response: loginResponse } = await CustomerModel.customerSignup(
        signupData
      )
      // @todo: figure out why this is data.data instead of just plain ol data
      let user = _get(loginResponse, 'data.data')
      dispatch('setUser', user)
      dispatch('setToken', user.token)
      return user
    } catch (error) {
      commit(Mtxs.SET_AUTH_LOGGED_OUT)
      return Promise.reject(error)
    }
  },

  async customerLogin({ commit, dispatch }, credentials) {
    try {
      let { response: loginResponse } = await CustomerModel.customerLogin(
        credentials
      )
      // @todo: figure out why this is data.data instead of just plain ol data
      let user = _get(loginResponse, 'data.data')
      dispatch('setUser', user)
      dispatch('setToken', user.token)
      dispatch('setExpiry', user.expires_at)
      return user
    } catch (error) {
      commit(Mtxs.SET_AUTH_LOGGED_OUT)
      return Promise.reject(error)
    }
  },

  async logout({ commit, dispatch, getters }) {
    try {
      let isMerchant = getters.canMerchant
      const isAdminAuthRoute = _includes(
        router.currentRoute.path.split('/'),
        ADMIN_URI_BASE
      )

      let LoginRouteName =
        isAdminAuthRoute || isMerchant
          ? MerchantRoutes.LOGIN
          : CustomerRoutes.LOGIN

      await UserModel.logout()

      await dispatch('unsetLaravelAuthHeaders')
      commit(Mtxs.SET_AUTH_LOGGED_OUT)
      // Clear all entity cache re:Orders, Items, Carts, etc
      await Object.values(AllModels)
        .filter((Model) => Model !== ShopModel)
        .forEach((Model) => Model.deleteAll())
      router.push({
        name: LoginRouteName,
        query: { loggedOut: true },
      })
    } catch (error) {
      return Promise.reject(error)
    }
  },

  async checkEmailAvailability({ rootGetters }, email) {
    let exists = false
    let shopId = rootGetters['shop/getShopId']
    try {
      let { data } = await Laravel.post(`/shops/${shopId}/email-exists/`, {
        email,
      })
      exists = data.exists
    } catch (error) {
      return Promise.reject(error)
    }
    return exists
  }, // checkEmailAvailability

  async fetchAuthMerchant({ dispatch }) {
    try {
      let res = await MerchantModel.fetchCurrent()
      dispatch('setUser', res.entities.merchants[0])
    } catch (error) {
      return Promise.reject(
        new Error(JSON.stringify(error.response ? error.response.data : error))
      )
    }
  }, // fetchAuthMerchant

  async fetchAuthCustomer({ dispatch }) {
    try {
      let res = await CustomerModel.fetchCurrent()
      dispatch('setUser', res.entities.customers[0])
    } catch (error) {
      return Promise.reject(
        new Error(JSON.stringify(error.response ? error.response.data : error))
      )
    }
  }, // fetchAuthCustomer

  // Validates the current user's token and refreshes it
  // with new data from the API.
  async validateSession({ dispatch }) {
    // If we haven't set the Laravel bearer token yet
    // try to do that with the token in store
    if (!Laravel.defaults.headers.common.Authorization) {
      await dispatch('setLaravelAuthHeaders')
    }

    try {
      let { data: validationRes } = await Laravel.get('/validate-token')
      await dispatch('setLaravelAuthHeaders', validationRes.token)
      await dispatch('setToken', validationRes.token)
      await dispatch('setExpiry', validationRes.expires_at)
      await dispatch('updateUser', {
        email_verified_at: validationRes.email_verified_at,
      })
      return validationRes
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch('logout')
        return false
      }
    }
  },

  setToken({ commit }, token) {
    commit(Mtxs.SET_TOKEN, token)
  },

  setExpiry({ commit }, expiry) {
    commit(Mtxs.SET_AUTH_EXPIRY, expiry)
  },

  setUser({ commit }, user) {
    commit(Mtxs.SET_AUTH_USER, user)
  },

  updateUser({ commit, getters }, data) {
    let userWithUpdates = { ...getters.getAuthUser, ...data }
    commit(Mtxs.SET_AUTH_USER, userWithUpdates)
  },

  unsetLaravelAuthHeaders() {
    Laravel.defaults.headers.common.Authorization = ''
  },

  setLaravelAuthHeaders({ state }, token) {
    token = token ? token : state.token
    Laravel.defaults.headers.common.Authorization = token
      ? `Bearer ${token}`
      : ''
  }, // setLaravelAuthHeaders

  async sendVerificationEmail({ dispatch, rootGetters }) {
    const shopId = rootGetters['shop/getShopId']
    if (!shopId) {
      return Promise.reject(
        new Error(
          `Could not send verification email because rootGetters['shop/getShopId'] is undefined`
        )
      )
    }
    dispatch('recordGtagSendVerification', { shopId })

    return Laravel.post(`shops/${shopId}/email/verify`)
  }, // sendPasswordResetEmail

  async checkIsMerchantForShop({ dispatch, rootGetters }, token) {
    const shopId = rootGetters['shop/getShopId']
    let isMerchantForShop = false
    try {
      await dispatch('setLaravelAuthHeaders', token)
      let { response: res } = await UserModel.fetchCurrent()
      let me = res.data.data
      if (me.is_merchant) {
        isMerchantForShop = shopId === me.shop_id
      } else {
        isMerchantForShop = false
      }
      await dispatch('setUser', me)
      return isMerchantForShop
    } catch (error) {
      return Promise.reject(error)
    }
  }, // checkIsMerchantForShop

  async sendPasswordResetEmail({ rootGetters }, email) {
    const shopId = rootGetters['shop/getShopId']
    return Laravel.post(`shops/${shopId}/password/email`, { email })
  }, // sendPasswordResetEmail

  async sendForgottenEmailEmail({ rootGetters }) {
    const shopId = rootGetters['shop/getShopId']
    return Laravel.post(`shops/${shopId}/merchant/forgot-email`)
  }, // sendForgottenEmailEmail

  async resetPassword({ rootGetters }, { token, email, password }) {
    const shopId = rootGetters['shop/getShopId']
    return Laravel.post(`shops/${shopId}/password/reset`, {
      token,
      email,
      password,
    })
  }, // resetPassword

  updateMerchantUser({ getters }, userData) {
    let uri = `/merchants/${getters.getAuthUserId}`
    return Laravel.patch(uri, userData)
  }, // updateMerchantUser

  updateCustomerUser({ getters }, userData) {
    let uri = `/customers/${getters.getAuthUserId}`
    return Laravel.patch(uri, userData)
  }, // updateCustomerUser

  async setIsAdminDrawerMini({ dispatch }, { is_admin_drawer_mini }) {
    try {
      dispatch('updateUser', { is_admin_drawer_mini })
      await dispatch('updateMerchantUser', { is_admin_drawer_mini })
    } catch (error) {
      return Promise.reject(error)
    }
  }, // setIsAdminDrawerMini

  setupFullStory({ getters }) {
    // Setup FullStory user tracking
    if (
      typeof window !== 'undefined' &&
      typeof window.FS !== 'undefined' &&
      getters.getAuthUser
    ) {
      window.FS.identify(getters.getAuthUserId, {
        displayName: getters.getAuthUserFullName,
      })
    }
  }, // setupFullStory

  recordGtagVerifyAttempt(_, { userId, shopId }) {
    if (Vue.$gtag && userId && shopId) {
      Vue.$gtag.event('verify_email', {
        event_category: 'auth',
        event_label: `/shops/${shopId}/customers/${userId}`,
      })
    }
  }, // recordGtagVerifyAttempt

  async recordGtagSendVerification({ getters }, { shopId }) {
    const userId = await getters.getAuthUserId
    if (Vue.$gtag && userId && shopId) {
      Vue.$gtag.event('send_verify_email', {
        event_category: 'auth',
        event_label: `/shops/${shopId}/customers/${userId}`,
      })
    }
  }, // recordGtagSendVerification
}
